import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import './LeaveRequestPage.css';

// Import Redux actions and selectors (matching reference)
import UpdateLeaveActions from '../../../redux/UpdateLeaveRedux';
import { UpdateLeaveSelectors } from '../../../redux/UpdateLeaveRedux';
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import { UploadImageToGoalActions } from '../../../redux/UploadImageToGoalRedux';
import { DeleteImageFromGoalActions } from '../../../redux/DeleteImageFromGoalRedux';

// Import components
import UploadFileComponent from '../../../components/UploadFileComponent';
import AlertBox from '../../../components/AlertBox/AlertBox';

// Import icons
import { ArrowLeft, ArrowRight } from '../../../assets/icons';

// ============================================================================
// PHASE 1: BASIC STRUCTURE SETUP
// ============================================================================

interface LeaveRequestPageProps extends WithTranslation {
  // Navigation props (will be provided by wrapper)
  leave_id?: string;
  children_id?: string;
  categoryId?: string;
  timeslotId?: string;
  childId?: string;
  code?: string;
  
  // Redux props (matching reference)
  currentSettings?: any;
  updateLeaveFetching?: boolean;
  updateLeaveSuccess?: boolean;
  updateLeaveError?: boolean;
  uploadImageToGoalFetching?: boolean;
  uploadImageToGoalSuccess?: boolean;
  uploadImageToGoalError?: boolean;
  deleteImageFromGoalFetching?: boolean;
  deleteImageFromGoalSuccess?: boolean;
  deleteImageFromGoalError?: boolean;
  
  // Navigation functions
  navigate?: (path: string, params?: any) => void;
  goBack?: () => void;
  
  // Redux actions (matching reference)
  updateLeave?: (leave_id: string, leave: any) => void;
  getChildren?: () => void;
  uploadImageToGoal?: (id: string, image: string) => void;
  deleteImageFromGoal?: (id: string, imageId: string) => void;
}

interface LeaveRequestPageState {
  // Phase 1: Basic state (matching reference)
  loading: boolean;
  isChanged: boolean;
  
  // Phase 3: Redux state handling
  props?: LeaveRequestPageProps; // For getDerivedStateFromProps
  
  // Phase 4: UI state
  alertTitle: string;
  alertMessage: string;
  alertPopupOpen: boolean;
}

class LeaveRequestPage extends Component<LeaveRequestPageProps, LeaveRequestPageState> {
  // Class properties (matching reference structure)
  photoLimit = 4;
  dropdown = false;
  leave = 0;
  
  constructor(props: LeaveRequestPageProps) {
    super(props);
    
    // Phase 1: Initialize basic state (matching reference)
    this.state = {
      loading: false,
      isChanged: false,
      
      // Phase 4: Initialize UI state
      alertTitle: '',
      alertMessage: '',
      alertPopupOpen: false,
    };
  }

  // ============================================================================
  // PHASE 1: BASIC LIFECYCLE METHODS
  // ============================================================================

  componentDidMount() {
    console.log('LeaveRequestPage mounted');
    // Phase 1: Basic mounting logic (matching reference)
    
    // Add click outside handler for dropdown
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    // Phase 1: Basic cleanup
    // Phase 2: Remove event listeners
    document.removeEventListener('click', this.handleClickOutside);
  }

  // ============================================================================
  // PHASE 2: CORE FUNCTIONALITY METHODS
  // ============================================================================
  
  // Handle leave type selection (matching reference)
  _onLeave = (leaveType: number) => {
    this.leave = leaveType;
    this.forceUpdate();
  };

  // Toggle dropdown visibility (matching reference)
  _onDropdown = () => {
    this.dropdown = !this.dropdown;
    this.forceUpdate();
  };

  // Handle next button press (matching reference)
  _onNextPress = () => {
    let leave = {
      type_of_leave: this.leave,
    };
    this.props.updateLeave?.(this.props.leave_id!, leave);
    
    if (this.props.updateLeaveSuccess && !this.props.updateLeaveFetching) {
      setTimeout(() => {
        this.props.navigate?.('/calendar/verify-request', {
          leave_id: this.props.leave_id,
          children_id: this.props.children_id,
          categoryId: this.props.categoryId,
          timeslotId: this.props.timeslotId,
          childId: this.props.childId,
          code: this.props.code,
          checkLeave: this.leave,
        });
      }, 500);
    }
  };

  // Handle back button press
  _back = () => {
    // Phase 2: Implement back button with confirmation
    if (this.state.isChanged) {
      this.showAlert(
        this.props.t('goal-comment-screen:alert.back.title'),
        this.props.t('goal-comment-screen:alert.back.message'),
        [
          { text: this.props.t('goal-comment-screen:alert.back.cancel') },
          { text: this.props.t('goal-comment-screen:alert.back.confirm'), action: () => this.props.goBack?.() }
        ]
      );
    } else {
      this.props.goBack?.();
    }
  };

  // ============================================================================
  // PHASE 3: REDUX INTEGRATION METHODS
  // ============================================================================
  
  // Handle Redux state changes (matching reference)
  static getDerivedStateFromProps(nextProps: any, prevState: LeaveRequestPageState) {
    if (prevState.props) {
      if (!nextProps.uploadImageToGoalFetching) {
        if (
          nextProps.uploadImageToGoalSuccess &&
          nextProps.uploadImageToGoalSuccess !==
            prevState.props.uploadImageToGoalSuccess
        ) {
          nextProps.getChildren?.();
          return {
            isChanged: false,
            props: nextProps,
          };
        }

        if (
          nextProps.uploadImageToGoalError &&
          nextProps.uploadImageToGoalError !==
            prevState.props.uploadImageToGoalError
        ) {
          // Show error alert would go here
          return {
            props: nextProps,
          };
        }
      }

      if (!nextProps.deleteImageFromGoalFetching) {
        if (
          nextProps.deleteImageFromGoalSuccess &&
          nextProps.deleteImageFromGoalSuccess !==
            prevState.props.deleteImageFromGoalSuccess
        ) {
          nextProps.getChildren?.();
          return {
            isChanged: false,
            props: nextProps,
          };
        }

        if (
          nextProps.deleteImageFromGoalError &&
          nextProps.deleteImageFromGoalError !==
            prevState.props.deleteImageFromGoalError
        ) {
          // Show error alert would go here
          return {
            props: nextProps,
          };
        }
      }
    }

    // Return null to indicate no change to state.
    return {
      props: nextProps,
    };
  }

  // ============================================================================
  // PHASE 4: UI COMPONENT METHODS
  // ============================================================================
  
  // Show alert dialog
  showAlert = (title: string, message: string, buttons?: Array<{text: string, action?: () => void}>) => {
    // Phase 4: Implement alert dialog
    this.setState({
      alertTitle: title,
      alertMessage: message,
      alertPopupOpen: true,
    });
  };

  // Close alert dialog
  closeAlert = () => {
    // Phase 4: Close alert dialog
    this.setState({ alertPopupOpen: false });
  };

  // Handle dropdown item click (matching reference)
  handleDropdownItemClick = (leaveType: number) => {
    this.leave = leaveType;
    this._onDropdown();
  };

  // Handle click outside dropdown to close it (matching reference)
  handleClickOutside = (event: MouseEvent) => {
    const dropdownContainer = document.querySelector('.dropdown-container');
    if (dropdownContainer && !dropdownContainer.contains(event.target as Node)) {
      if (this.dropdown) {
        this.dropdown = false;
        this.forceUpdate();
      }
    }
  };

  // ============================================================================
  // PHASE 5: RENDER METHODS
  // ============================================================================
  
  // Render header component
  renderHeader = () => {
    // Phase 4: Render header with back button, title, and next button
    return (
      <header className="page-header">
        <div className="page-header-inner">
          <div className="header-left">
            <button 
              className="header-button back-button"
              onClick={this._back}
              type="button"
            >
              <ArrowLeft />
            </button>
            <h1 className="header-title">
              {this.props.t('verify-request:leave-request')}
            </h1>
          </div>
          <div className="header-right">
            <button 
              className="next-button header-button"
              onClick={this._onNextPress}
              disabled={this.props.updateLeaveFetching}
            >
              <span className="button-text">
                {this.props.t('select-date-and-time-screen:header.right')}
              </span>
            </button>
          </div>
        </div>
      </header>
    );
  };

  // Render leave type dropdown
  renderLeaveTypeDropdown = () => {
    // Phase 4: Render leave type selection dropdown
    return (
      <div className="leave-type-section">
        <h2 className="section-header uppercase">
          {this.props.t('select-leavere-quest:type-of-leave')}
        </h2>
        
        <div className="dropdown-container">
          <div 
            className={`dropdown-select ${this.dropdown ? 'open' : ''}`}
            onClick={this._onDropdown}
          >
            <span className="dropdown-text">
              {this.leave === 1 
                ? this.props.t('select-leavere-quest:sick-leave')
                : this.props.t('select-leavere-quest:other-leave')
              }
            </span>
            <div className="dropdown-arrow">
              <ArrowRight style={{ transform: 'rotate(90deg)' }} />
            </div>
          </div>
          
          {this.dropdown && (
            <div className="dropdown-menu">
              <button 
                className="dropdown-item"
                onClick={() => this.handleDropdownItemClick(0)}
              >
                {this.props.t('select-leavere-quest:other-leave')}
              </button>
              <button 
                className="dropdown-item"
                onClick={() => this.handleDropdownItemClick(1)}
              >
                {this.props.t('select-leavere-quest:sick-leave')}
              </button>
            </div>
          )}
        </div>
        
        <p className="dropdown-note">
          {this.props.t('verify-request:leave-request-is-only')}
        </p>
      </div>
    );
  };

  // Render file upload section (matching reference)
  renderFileUploadSection = () => {
    if (this.leave !== 1) return null;
    
    return (
      <div className="file-upload-section">
        <h2 className="section-header">
          {this.props.t('verify-request:upload-sick-note')}
        </h2>
        <UploadFileComponent
          leave_id={this.props.leave_id}
          children_id={this.props.children_id}
          isSickImage={false}
        />
      </div>
    );
  };

  // Main render method
  render() {
    const { loading, alertPopupOpen, alertTitle, alertMessage } = this.state;
    const { t, updateLeaveFetching } = this.props;

    return (
      <div className="leave-request-page">
        {/* Phase 1: Basic structure */}
        <div className="leave-request-container">
          
          {/* Phase 4: Header component */}
          {this.renderHeader()}
          
          {/* Phase 4: Main content */}
          <main className="leave-request-content">
            
            {/* Phase 4: Leave type dropdown */}
            {this.renderLeaveTypeDropdown()}
            
            {/* Phase 4: File upload section */}
            {this.renderFileUploadSection()}
            
            
          </main>
        </div>
        
        {/* Phase 4: Alert dialog */}
        <AlertBox
          title={alertTitle}
          message={alertMessage}
          okAction={this.closeAlert}
          isVisible={alertPopupOpen}
          onClose={this.closeAlert}
        />
      </div>
    );
  }
}

// ============================================================================
// PHASE 3: REDUX CONNECTION
// ============================================================================

// Map state to props (matching reference)
const mapStateToProps = (state: any) => ({
  currentSettings: state.currentSettings,
  uploadImageToGoalFetching: state.uploadImageToGoal?.fetching,
  uploadImageToGoalSuccess: state.uploadImageToGoal?.success,
  uploadImageToGoalError: state.uploadImageToGoal?.error,
  deleteImageFromGoalFetching: state.deleteImageFromGoal?.fetching,
  deleteImageFromGoalSuccess: state.deleteImageFromGoal?.success,
  deleteImageFromGoalError: state.deleteImageFromGoal?.error,
  updateLeaveFetching: state.updateLeave?.fetching,
  updateLeaveSuccess: state.updateLeave?.success,
  updateLeaveError: state.updateLeave?.error,
});

// Map dispatch to props (matching reference)
const mapDispatchToProps = (dispatch: any) => ({
  uploadImageToGoal: (id: string, image: string) =>
    dispatch(UploadImageToGoalActions.uploadImageToGoalRequest(id, image)),
  deleteImageFromGoal: (id: string, imageId: string) =>
    dispatch(DeleteImageFromGoalActions.deleteImageFromGoalRequest(id, imageId)),
  getChildren: () => dispatch(GetChildrenActions.getChildrenRequest()),
  updateLeave: (leave_id: string, leave: any) =>
    dispatch(UpdateLeaveActions.updateLeaveRequest(leave_id, leave)),
});

// Export with Redux connection
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LeaveRequestPage));
